import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { showSuccessToast, showErrorToast, ImagePreview } from '@/components';
import {
  getDeliveryDetail,
  DeliveryDetail,
  DeliveryDetailLocation,
  DeliveryParty,
} from '@/services/api/deliveries';
import {
  DeliveryStatusBadge,
  getDeliveryStatusLabel,
} from './DeliveryStatusBadge';

const fmt = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—';

const partyName = (p?: DeliveryParty) => {
  if (!p) return '—';
  if (p.companyName) return p.companyName;
  const n = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  return n || '—';
};

const mapsHref = (loc?: DeliveryDetailLocation) => {
  if (!loc) return undefined;
  const { latitude, longitude } = loc.coordinates ?? {};
  const q =
    latitude != null && longitude != null
      ? `${latitude},${longitude}`
      : loc.address;
  return q
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    : undefined;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
    <h3 className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {title}
    </h3>
    {children}
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({
  k,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="text-text-sub-600">{k}</span>
    <span className="text-right font-semibold text-text-strong-950">
      {children}
    </span>
  </div>
);

const SlaCard: React.FC<{
  label: string;
  status?: 'ON_TIME' | 'LATE' | null;
  deadlineLabel: string;
  deadline?: string | null;
}> = ({ label, status, deadlineLabel, deadline }) => (
  <div className="rounded-lg border border-stroke-soft-200 p-3.5">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {label}
    </div>
    {status === 'ON_TIME' ? (
      <span className="inline-flex items-center rounded-full bg-success-lighter px-2.5 py-0.5 text-xs font-medium text-success-dark">
        On time
      </span>
    ) : status === 'LATE' ? (
      <span className="inline-flex items-center rounded-full bg-error-lighter px-2.5 py-0.5 text-xs font-medium text-error-dark">
        Late
      </span>
    ) : (
      <span className="text-sm text-text-soft-400">—</span>
    )}
    {/* The deadline the status above is judged against. */}
    <div className="mt-3 border-t border-stroke-soft-200 pt-2.5">
      <div className="text-[11px] text-text-sub-600">{deadlineLabel}</div>
      <div className="mt-0.5 font-mono text-[12.5px] font-semibold text-text-strong-950">
        {fmt(deadline)}
      </div>
    </div>
  </div>
);

const TERMINAL_BAD = new Set([
  'FAILED',
  'CANCELLED',
  'RETURNED',
  'FAILED_IN_RETURN',
  'DISPOSED',
]);

const RouteNode: React.FC<{
  kind: 'pickup' | 'dropoff';
  name: string;
  loc?: DeliveryDetailLocation;
  phone?: string;
  showConnector?: boolean; // draw the line linking this dot to the next node
}> = ({ kind, name, loc, phone, showConnector }) => {
  const href = mapsHref(loc);
  const coords = loc?.coordinates;
  return (
    <div className="flex gap-3">
      {/* Dot + connecting line */}
      <div className="flex flex-col items-center self-stretch">
        <span
          className={`mt-0.5 h-[18px] w-[18px] shrink-0 rounded-full border-2 ${
            kind === 'pickup'
              ? 'border-primary bg-bg-white-0'
              : 'border-success-base bg-success-base'
          }`}
        />
        {showConnector && (
          <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />
        )}
      </div>
      <div className={`min-w-0 flex-1 ${showConnector ? 'pb-5' : ''}`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
          {kind === 'pickup' ? 'Pickup' : 'Drop-off'}
        </div>
        <div className="mt-0.5 font-semibold text-text-strong-950">{name}</div>
        {loc?.address && (
          <div className="mt-0.5 text-[13px] leading-snug text-text-strong-950">
            {loc.address}
          </div>
        )}
        {coords?.latitude != null && coords?.longitude != null && (
          <div className="mt-1 font-mono text-[11.5px] text-text-soft-400">
            {coords.latitude}, {coords.longitude}
          </div>
        )}
        {phone && (
          <div className="mt-0.5 font-mono text-xs text-text-sub-600">
            {phone}
          </div>
        )}
        {/* The instruction the rider is shown for this stop. Boxed rather than
            run on after the address so it doesn't read as more address text —
            it is something a person has to act on. */}
        {loc?.notes && (
          <div className="mt-2 flex items-start gap-1.5 rounded-md border border-stroke-soft-200 bg-bg-weak-50 px-2.5 py-1.5">
            <i
              className="ri-sticky-note-line mt-px shrink-0 text-[13px] text-text-soft-400"
              aria-hidden="true"
            />
            <p className="text-[12.5px] leading-snug text-text-sub-600">
              <span className="font-semibold text-text-strong-950">
                {kind === 'pickup' ? 'Catatan pickup' : 'Catatan pengantaran'}:
              </span>{' '}
              {loc.notes}
            </p>
          </div>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-2.5 py-1 text-xs font-medium text-text-sub-600 hover:bg-bg-weak-50"
          >
            <i className="ri-map-pin-line" />
            Open in Maps
          </a>
        )}
      </div>
    </div>
  );
};

interface DeliveryDetailDrawerProps {
  deliveryID: string | null;
  onClose: () => void;
}

const DeliveryDetailDrawer: React.FC<DeliveryDetailDrawerProps> = ({
  deliveryID,
  onClose,
}) => {
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setDetail(null);
    try {
      setDetail(await getDeliveryDetail(id));
    } catch (error) {
      showErrorToast(
        'Gagal memuat detail',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
      onClose();
    } finally {
      setLoading(false);
    }
    // onClose is stable enough; intentionally not a dep to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deliveryID) load(deliveryID);
  }, [deliveryID, load]);

  if (!deliveryID) return null;

  const copyPhone = (phone?: string) => {
    if (!phone) return;
    navigator.clipboard?.writeText(phone);
    showSuccessToast('Disalin', `Nomor disalin — ${phone}`);
  };

  const q = detail?.quote;
  const driverInitials = (detail?.courier?.name ?? '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[640px] max-w-[95vw] flex-col bg-bg-white-0 shadow-card-lg">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-stroke-soft-200 px-6 py-5">
          <div className="min-w-0">
            {detail && (
              <div className="mb-2">
                <DeliveryStatusBadge status={detail.status} />
              </div>
            )}
            <h2 className="truncate font-mono text-xl font-semibold tracking-tight text-text-strong-950">
              {detail?.deliveryID ?? deliveryID}
            </h2>
            {detail?.providerOrderID && (
              <p className="mt-0.5 font-mono text-xs text-text-soft-400">
                Booking ID ·{' '}
                <span className="font-semibold text-text-sub-600">
                  {detail.providerOrderID}
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        {loading || !detail ? (
          <div className="flex flex-1 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-text-soft-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Parties */}
            <Section title="Parties">
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    { label: 'Sender', party: detail.sender },
                    { label: 'Recipient', party: detail.recipient },
                  ] as const
                ).map(({ label, party }) => (
                  <div
                    key={label}
                    className="rounded-lg border border-black-500 p-3.5"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
                      {label}
                    </div>
                    <div className="mt-2 font-semibold text-text-strong-950">
                      {partyName(party)}
                    </div>
                    {party?.phone && (
                      <div className="mt-0.5 font-mono text-xs text-text-sub-600">
                        {party.phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* Route */}
            <Section
              title={`Delivery Route${
                q?.distance ? ` · ${(q.distance / 1000).toFixed(1)} km` : ''
              }`}
            >
              <RouteNode
                kind="pickup"
                name={partyName(detail.sender)}
                loc={q?.origin}
                showConnector
              />
              <RouteNode
                kind="dropoff"
                name={partyName(detail.recipient)}
                loc={q?.destination}
                phone={detail.recipient?.phone}
              />
            </Section>

            {/* Data Barang */}
            <Section title="Data Barang">
              {q?.packages && q.packages.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-stroke-soft-200">
                  <table className="w-full text-sm">
                    <thead className="bg-bg-weak-50 text-xs text-text-soft-400">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Item
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Qty
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Weight
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stroke-soft-200">
                      {q.packages.map((p, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-text-strong-950">
                            {p.name || '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-text-sub-600">
                            {p.quantity ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-text-sub-600">
                            {p.weight != null ? `${p.weight} kg` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-text-soft-400">No items</p>
              )}
            </Section>

            {/* Service */}
            <Section title="Service">
              <KV k="Service">{q?.service?.name || q?.service?.type || '—'}</KV>
              <KV k="Payment method">{detail.paymentMethod || '—'}</KV>
              <KV k="COD">
                {(detail.cashOnDelivery?.amount ?? 0) > 0
                  ? `${q?.currency?.symbol ?? 'Rp'} ${detail.cashOnDelivery?.amount?.toLocaleString()}`
                  : 'Non-COD'}
              </KV>
              <KV k="Pickup window">
                {detail.schedule?.pickupTimeFrom
                  ? `${fmt(detail.schedule.pickupTimeFrom)}${
                      detail.schedule.pickupTimeTo
                        ? ` – ${dayjs(detail.schedule.pickupTimeTo).format('HH:mm')}`
                        : ''
                    }`
                  : '—'}
              </KV>
            </Section>

            {/* Invoice */}
            {detail.invoiceNo && (
              <Section title="Invoice Info">
                <KV k="Invoice #">
                  <span className="font-mono">{detail.invoiceNo}</span>
                </KV>
              </Section>
            )}

            {/* Driver */}
            <Section title="Driver Details">
              {detail.courier?.name ? (
                <>
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-static-white">
                      {driverInitials || '—'}
                    </div>
                    <div>
                      <div className="font-medium text-text-strong-950">
                        {detail.courier.name}
                      </div>
                      {detail.courier.phone && (
                        <div className="font-mono text-xs text-text-sub-600">
                          {detail.courier.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  {detail.courier.phone && (
                    <button
                      type="button"
                      onClick={() => copyPhone(detail.courier?.phone)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 py-2 text-sm font-medium text-text-sub-600 hover:bg-bg-weak-50"
                    >
                      <i className="ri-file-copy-line" />
                      Copy number
                    </button>
                  )}
                </>
              ) : (
                <p className="text-sm text-text-soft-400">
                  Driver belum ditugaskan
                </p>
              )}
            </Section>

            {/* Live Tracking — hidden entirely when there is no tracking URL */}
            {detail.trackingURL && detail.trackingURL.trim() !== '' && (
              <Section title="Live Tracking">
                <a
                  href={detail.trackingURL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 py-2 text-sm font-medium text-text-sub-600 hover:bg-bg-weak-50"
                >
                  Open public tracker
                  <i className="ri-arrow-right-up-line" />
                </a>
              </Section>
            )}

            {/* SLA */}
            <Section title="Delivery SLA">
              <div className="grid grid-cols-2 gap-3">
                <SlaCard
                  label="Pickup SLA"
                  status={detail.pickup_sla_status}
                  deadlineLabel="Max pickup at"
                  deadline={detail.max_pickup_at}
                />
                <SlaCard
                  label="Delivery SLA"
                  status={detail.complete_sla_status}
                  deadlineLabel="Max completion at"
                  deadline={detail.max_completion_at}
                />
              </div>
            </Section>

            {/* Progress */}
            <Section title="Delivery Progress">
              {detail.timeline && detail.timeline.length > 0 ? (
                <ol className="space-y-0">
                  {detail.timeline.map((e, i) => {
                    const last = i === detail.timeline!.length - 1;
                    const bad = TERMINAL_BAD.has(e.status ?? '');
                    // Past steps = done (green); the latest step is highlighted
                    // (primary ring) unless it's a terminal failure (red).
                    const dotClass = !last
                      ? 'h-4 w-4 border-2 border-success-base bg-success-base'
                      : bad
                        ? 'h-4 w-4 border-2 border-error-base bg-error-base'
                        : 'h-4 w-4 border-2 border-primary bg-primary ring-4 ring-feature-lighter';
                    return (
                      <li key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <span
                            className={`mt-0.5 shrink-0 rounded-full ${dotClass}`}
                          />
                          {!last && (
                            <span className="my-1 w-0.5 flex-1 bg-success-base/40" />
                          )}
                        </div>
                        <div className="pb-5">
                          <div className="text-[13.5px] font-semibold text-text-strong-950">
                            {getDeliveryStatusLabel(e.status ?? '')}
                          </div>
                          <div className="mt-0.5 font-mono text-[11.5px] text-text-soft-400">
                            {fmt(e.createdAt ?? e.created_at)}
                          </div>
                          {e.notes && (
                            <div className="text-xs text-text-sub-600">
                              {e.notes}
                            </div>
                          )}
                          {e.media && e.media.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {e.media
                                .filter((m) => m?.url)
                                .map((m, mi) => (
                                  <div key={mi} className="h-16 w-16">
                                    <ImagePreview
                                      src={m.url}
                                      alt={m.type || 'Proof'}
                                      className="h-16 w-16 rounded-lg border border-stroke-soft-200 object-cover"
                                    />
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="text-sm text-text-soft-400">No progress yet</p>
              )}
            </Section>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default DeliveryDetailDrawer;
